import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { CheckCircle2, Loader2, Clock } from "lucide-react";
import { trackFunnel } from "../lib/track";

const eur = (c) => `€${((c || 0) / 100).toFixed(2)}`;
const METHOD = { stripe: "Carta di credito (Stripe)", paypal: "PayPal", promotion: "Promozione di lancio", promo: "Promozione" };
const MAX_POLLS = 24;

function Summary({ o }) {
  const rows = [["Piano", o.plan_name || o.plan_code], o.rally_name && ["Rally", o.rally_name], ["Imponibile", eur(o.net_cents)], o.discount_cents > 0 && [`Sconto ${o.coupon_code || ""}`, `− ${eur(o.discount_cents)}`], [`IVA ${o.vat_pct || 22}%`, eur(o.tax_cents)], ["Totale", eur(o.total_cents)], ["Metodo", METHOD[o.provider] || o.provider], ["Data", (o.paid_at || o.created_at || "").slice(0, 16).replace("T", " ")], ["Ordine", o.id?.slice(0, 8).toUpperCase()], ["Stato licenza", o.license_status === "ACTIVE" ? "Attiva" : o.license_status === "VERIFYING" ? "In verifica" : o.license_status || "—"]].filter(Boolean);
  return <div className="rl-card p-4 text-left text-sm mb-6" data-testid="payment-summary">{rows.map(([l, v]) => <div key={l} className="flex justify-between gap-3 border-b border-white/5 py-1.5"><span className="text-slate-400">{l}</span><span className={l === "Totale" ? "font-display font-black" : ""} data-testid={`summary-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</span></div>)}</div>;
}

export default function PaymentSuccess() {
  const [params] = useSearchParams(); const nav = useNavigate();
  const oid = params.get("order_id"); const sid = params.get("session_id"); const free = params.get("free") === "1";
  const [order, setOrder] = useState(null); const [status, setStatus] = useState(free ? "paid" : "checking"); const [tries, setTries] = useState(0);

  useEffect(() => {
    if (free) return;
    if (!oid && !sid) { nav("/"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        if (oid) {
          const { data } = await axios.get(`${API}/payments/order/${oid}`); if (cancelled) return; setOrder(data);
          if (data.status === "PAID" && data.license_status === "ACTIVE") { setStatus("paid"); trackFunnel("checkout_completed", { plan: data.plan_code }); trackFunnel("license_active", { plan: data.plan_code }); return; }
          if (["CANCELLED", "EXPIRED", "FAILED"].includes(data.status)) { nav(`/payment/cancel?order_id=${oid}${data.status === "FAILED" ? "&failed=1" : ""}`); return; }
          if (data.status === "PAID") { setStatus("verifying"); }
        } else {
          const { data } = await axios.get(`${API}/payments/status/${sid}`); if (cancelled) return;
          if (data.payment_status === "paid") { setStatus("paid"); trackFunnel("checkout_completed", { sid }); return; }
        }
        if (tries < MAX_POLLS) { setTries(t => t + 1); setTimeout(poll, 2500); } else setStatus("timeout");
      } catch { if (tries < MAX_POLLS) { setTries(t => t + 1); setTimeout(poll, 2500); } else setStatus("timeout"); }
    };
    poll();
    return () => { cancelled = true; };
  }, [oid, sid, tries, nav, free]);

  const isPromax = order?.kind === "PROMAX"; const next = order?.next_url || "/organizer/dashboard";
  return (
    <div className="min-h-screen grain-bg">
      <Navbar variant="pro" />
      <div className="pt-28 pb-24 max-w-2xl mx-auto px-5 text-center">
        {status === "checking" && (
          <div className="rl-card p-12" data-testid="payment-checking">
            <Loader2 className="w-12 h-12 text-[#FF1E27] mx-auto mb-4 animate-spin" />
            <div className="font-display font-black text-2xl">Stiamo confermando il pagamento…</div>
            <div className="text-sm text-slate-400 mt-2">Bastano pochi secondi. Non chiudere la pagina e non ripetere il pagamento.</div>
          </div>
        )}
        {status === "verifying" && order && (
          <div className="rl-card p-10" data-testid="payment-verifying">
            <Clock className="w-14 h-14 text-[#FF9F0A] mx-auto mb-4" />
            <div className="pill-badge pill-amber mb-3 inline-flex">PAGAMENTO RICEVUTO</div>
            <h1 className="font-display font-black text-2xl mb-2">Pagamento ricevuto. Stiamo verificando l'attivazione.</h1>
            <p className="text-sm text-slate-400 mb-6">La licenza viene attivata appena il provider conferma definitivamente l'incasso. Non serve pagare di nuovo.</p>
            <Summary o={order} />
            <button className="btn-outline-rl min-h-[48px]" onClick={() => nav(next)} data-testid="verifying-continue-btn">VAI ALL'AREA ORGANIZZATORE</button>
          </div>
        )}
        {status === "paid" && (
          <div className="rl-card p-8 md:p-12 rl-glow-crimson">
            <CheckCircle2 className="w-20 h-20 text-[#30D158] mx-auto mb-4" />
            <div className="pill-badge pill-green mb-4 inline-flex">PAGAMENTO COMPLETATO</div>
            <h1 className="headline-huge text-3xl md:text-5xl mb-4" data-testid="license-active-heading">{free ? "LA TUA LICENZA È ATTIVA" : "PAGAMENTO COMPLETATO"}</h1>
            <p className="text-slate-300 mb-6">{isPromax ? "La licenza PROMAX è attiva: ora configura il tuo rally e pubblica il primo documento." : "La tua licenza è attiva. Ora puoi attivare il tuo rally e informare i concorrenti."}</p>
            {order && <Summary o={order} />}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button className="btn-crimson min-h-[52px]" onClick={() => { trackFunnel("event_activated", { via: "cta" }); nav(next); }} data-testid="go-dashboard-btn">{isPromax ? "CONFIGURA IL TUO RALLY" : "ATTIVA ORA IL TUO RALLY"}</button>
              {!isPromax && <button className="btn-outline-rl min-h-[52px]" onClick={() => nav("/organizer/promax")} data-testid="go-promax-btn">APRI PROMAX</button>}
            </div>
            <p className="text-xs text-slate-500 mt-6">La ricevuta arriva via email. Serve aiuto? <a href="mailto:organizers@rallylove.it" className="text-[#FF9F0A] underline">Scrivici</a>.</p>
          </div>
        )}
        {status === "timeout" && (
          <div className="rl-card p-12" data-testid="payment-timeout">
            <div className="font-display font-black text-2xl mb-2">Stiamo ancora confermando il pagamento.</div>
            <p className="text-slate-400 mb-6">Nessun secondo addebito verrà effettuato. La licenza comparirà nella tua area organizzatore appena confermata. Se non compare entro 10 minuti, scrivici indicando l'ordine {oid?.slice(0, 8).toUpperCase()}.</p>
            {order && <Summary o={order} />}
            <button className="btn-outline-rl min-h-[48px]" onClick={() => nav(next)} data-testid="check-later-btn">VAI ALL'AREA ORGANIZZATORE</button>
          </div>
        )}
      </div>
    </div>
  );
}
